/**
 * CORE-964 — контракты реферального экрана (модель v3, деньги).
 *
 * Прежняя модель «hearts / agapes» снята: вознаграждение — реальные деньги,
 * баллов и внутреннего кошелька в v3 нет. Старые типы из `types/fund.ts`
 * к этому экрану отношения не имеют.
 */

/** Состояние начисления глазами пользователя. Приходит числом — это enum бэкенда. */
export enum ReferralAccrualState {
    Pending = 0,
    Available = 1,
    Paid = 2,
    Returned = 3,
    OnReview = 4,
}

/**
 * CORE-1156 — ответ `POST /api/referral/visit`: чем клиент обязан представиться
 * при регистрации, чтобы переход по ссылке стал связью.
 *
 * `visitorToken` — это тот же самый `X-Anon-User-Id`, что фронт уже хранит и шлёт
 * (второго идентификатора посетителя рефералка не заводит намеренно: второе место,
 * где он может потеряться, и есть способ потерять начисление).
 *
 * Ответ одинаков для живого, отозванного и несуществующего кода — иначе ручка
 * стала бы оракулом для перебора чужих кодов. Значит по нему нельзя судить,
 * засчитан переход или нет, и клиенту судить не нужно.
 */
export interface ReferralVisitResponse {
    visitorToken: string;
    expiresAtUtc: string;
}

export interface ReferralLink {
    id: number;
    url: string;
    sourceType: number;
    sourceId?: string | null;
    campaign?: string | null;
    createdAtUtc: string;
}

export interface ReferralIdentity {
    code: string;
    url: string;
    enrolled: boolean;
    termsVersion?: string | null;
    links: ReferralLink[];
    /**
     * CORE-1662 — чем программа вознаграждает этого человека. Решает сервер, тем
     * же правилом, что и движок начислений: `points` — обычный пользователь
     * российского контура, ему не показываются суммы и выплаты.
     */
    rewardModel?: ReferralRewardModel;
    /**
     * CORE-1071 — нужно ли для подключения принять «Условия реферальной программы»
     * галочкой. Решает сервер (флаг `referral_terms_document`, денежная модель);
     * поле отсутствует у сервера без этой правки — значит, не нужно.
     */
    termsDocumentRequired?: boolean;
    /** CORE-1071 — действующая редакция документа; null при `termsDocumentRequired` — редакции нет. */
    termsDocument?: ReferralTermsDocument | null;
    /** CORE-2078 — принята ли действующая редакция `termsDocument`. */
    termsDocumentAccepted?: boolean;
    /**
     * CORE-2078 — человек подключён, а действующую редакцию не принимал (вышла новая,
     * сменился контур или подключался до флага): виджет снова просит галочку, принятие —
     * тем же `enrollInReferralProgram`. До принятия начисления идут, выплата ждёт.
     */
    termsReacceptRequired?: boolean;
}

/** CORE-1071 — редакция «Условий реферальной программы» из реестра CORE-1660, без текста. */
export interface ReferralTermsDocument {
    documentId: string;
    version: string;
    variant: string;
    effectiveFrom: string;
    sha256: string;
}

export type ReferralRewardModel = 'money' | 'points';

/** Итоги по одной валюте. Валюты не смешиваются и не конвертируются. */
export interface ReferralTotals {
    currency: string;
    earnedMinor: number;
    availableMinor: number;
    paidMinor: number;
}

export interface ReferralSummary {
    totals: ReferralTotals[];
    nextPayoutDateUtc: string;
}

/**
 * CORE-2067 — баллы обычного пользователя российского контура по одной валюте
 * покупок. `*Minor` — стоимость, `*Points` — уже пересчитанные сервером баллы:
 * курс живёт в настройке бэкенда, клиент ничего не пересчитывает.
 */
export interface ReferralPointsTotals {
    currency: string;
    earnedMinor: number;
    availableMinor: number;
    pendingMinor: number;
    debtMinor: number;
    availablePoints: number;
    pendingPoints: number;
    earnedPoints: number;
    nextAvailableAtUtc?: string | null;
}

export interface ReferralPoints {
    rewardModel: ReferralRewardModel;
    totals: ReferralPointsTotals[];
}

export interface ReferralSourceBreakdown {
    sourceType: string;
    sourceId?: string | null;
    campaign?: string | null;
    clicks: number;
    registrations: number;
}

export interface ReferralFunnel {
    clicks: number;
    registrations: number;
    buyers: number;
    bySource: ReferralSourceBreakdown[];
}

export interface ReferralAccrual {
    dateUtc: string;
    level: number;
    amountMinor: number;
    currency: string;
    state: ReferralAccrualState;
    /** Заполнено только для Pending. */
    availableAtUtc?: string | null;
}

export interface ReferralPayout {
    period: string;
    amountMinor: number;
    currency: string;
    sentAtUtc: string;
}
